import { useState } from "react";
import { useRunAnalysis } from "@/sdk/hooks/use-scanner";
import { useCouncilAsk } from "@/sdk/hooks/use-council";
import { Panel } from "@/components/panel";
import { LoadingState } from "@/components/loading-state";
import type { CouncilVerdictWire } from "@/sdk/hooks/use-council";

// NOTE: the quick/standard/deep tabs are cosmetic — useRunAnalysis drops the
// `mode` param and always hits GET /api/scanner/market/{id}. Only "debate"
// changes behaviour (routes to the council). Kept as-is pending a backend
// mode param; removing them would hide the intended capability split.
type Tab = "quick" | "standard" | "deep" | "debate";
const TABS: Tab[] = ["quick", "standard", "deep", "debate"];

function describeError(error: unknown): string {
  // ApiError extends Error, so its normalized backend message lands here.
  if (error instanceof Error && error.message) return error.message;
  return "Request failed";
}

export function AnalysisPanel() {
  const [marketId, setMarketId] = useState("");
  const [tab, setTab] = useState<Tab>("standard");
  const runAnalysis = useRunAnalysis();
  const askCouncil = useCouncilAsk();
  const [councilResult, setCouncilResult] = useState<CouncilVerdictWire | null>(null);

  const handleRun = () => {
    if (tab === "debate") {
      // The council route accepts question/use_search only — no strategy param.
      askCouncil.mutate({ question: `Analyse prediction market: ${marketId}` }, { onSuccess: (v) => setCouncilResult(v) });
    } else {
      runAnalysis.mutate({ market_id: marketId, mode: tab });
    }
  };

  const isLoading = runAnalysis.isPending || askCouncil.isPending;
  const result = tab === "debate" ? councilResult : runAnalysis.data;
  const activeError =
    tab === "debate"
      ? askCouncil.isError
        ? askCouncil.error
        : null
      : runAnalysis.isError
        ? runAnalysis.error
        : null;

  return (
    <Panel title="Multi-Mode Analysis">
      <div className="space-y-3">
        <input className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none" placeholder="Market ID or question…" value={marketId} onChange={e => setMarketId(e.target.value)} />
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
          {TABS.map(t => (
            <button type="button" key={t} onClick={() => setTab(t)} className={`flex-1 py-1 rounded text-xs font-medium transition-colors ${tab === t ? "bg-white/20 text-white" : "text-white/40 hover:text-white/60"}`}>{t}</button>
          ))}
        </div>
        <button type="button" onClick={handleRun} disabled={isLoading || !marketId} className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-xs text-white font-medium">
          {isLoading ? "Running…" : `Run ${tab} analysis`}
        </button>
        {isLoading && <LoadingState text={`Running ${tab} analysis…`} />}
        {activeError != null && (
          <p role="alert" className="text-xs text-red-300">
            Analysis request failed: {describeError(activeError)}
          </p>
        )}
        {result && (
          <pre className="text-xs text-white/60 bg-white/5 rounded p-3 overflow-auto max-h-80 leading-relaxed">{JSON.stringify(result, null, 2)}</pre>
        )}
      </div>
    </Panel>
  );
}


